/**
 * Markdown → typed document model for article bodies.
 *
 * Articles are authored as Markdown (`posts.body_md_en` / `body_md_es`) and this
 * module turns that text into a small, explicit tree that `PostBody` maps onto
 * React elements. Two rules drive the design:
 *
 * 1. **No HTML ever leaves this module.** We lex with `marked` and read its
 *    tokens; we never ask it for an HTML string, so nothing can reach
 *    `dangerouslySetInnerHTML`. Raw HTML written in the source (`<b>hi</b>`)
 *    arrives as `html` tokens and is rendered as literal text.
 * 2. **GFM is off.** Bare URLs must stay plain text: article bibliographies are
 *    full of DOIs, and GFM's autolink literals would silently turn every one of
 *    them into a link — a visible change to copy the site owner has already
 *    signed off on. Emphasis, links, lists, quotes and fenced code are all
 *    CommonMark and unaffected.
 *
 * This module is isomorphic (the article page renders it on the server and
 * hydrates it in the browser; the admin preview runs it client-side), so it must
 * stay free of server-only imports.
 */

import { marked, type Token, type Tokens } from 'marked';

// ─── Document model ──────────────────────────────────────────────────────────

export type MdInline =
  | { kind: 'text'; value: string }
  | { kind: 'strong'; children: MdInline[] }
  | { kind: 'em'; children: MdInline[] }
  | { kind: 'del'; children: MdInline[] }
  | { kind: 'code'; value: string }
  | { kind: 'link'; href: string | null; children: MdInline[] }
  | { kind: 'break' };

export type MdListItem = { children: MdInline[]; lists: MdList[] };
export type MdList = { ordered: boolean; items: MdListItem[] };

export type MdBlock =
  | { kind: 'p'; children: MdInline[] }
  | { kind: 'h2'; children: MdInline[] }
  | { kind: 'h3'; children: MdInline[] }
  | { kind: 'quote'; paragraphs: MdInline[][] }
  | ({ kind: 'list' } & MdList)
  | { kind: 'code'; value: string }
  | { kind: 'hr' }
  /** The bibliography card. Never written as such — see `splitReferences`. */
  | { kind: 'refs'; items: MdInline[][] };

export type PostDocument = { blocks: MdBlock[] };

// ─── Link safety ─────────────────────────────────────────────────────────────

/** Schemes an author may link to. Anything else (notably `javascript:`) renders
    as plain text with no href rather than as a live link. */
const SAFE_SCHEME = /^(https?:|mailto:|tel:)/i;
/** Same-site and in-page targets carry no scheme at all. */
const RELATIVE_HREF = /^(\/|#|\.\.?\/)/;

function safeHref(href: string | null | undefined): string | null {
  const value = (href ?? '').trim();
  if (!value) return null;
  if (RELATIVE_HREF.test(value)) return value;
  return SAFE_SCHEME.test(value) ? value : null;
}

// ─── Inline tokens ───────────────────────────────────────────────────────────

function inlineFrom(tokens: Token[] | undefined): MdInline[] {
  const out: MdInline[] = [];
  if (!tokens) return out;

  for (const token of tokens) {
    switch (token.type) {
      case 'strong':
        out.push({ kind: 'strong', children: inlineFrom((token as Tokens.Strong).tokens) });
        break;
      case 'em':
        out.push({ kind: 'em', children: inlineFrom((token as Tokens.Em).tokens) });
        break;
      case 'del':
        out.push({ kind: 'del', children: inlineFrom((token as Tokens.Del).tokens) });
        break;
      case 'codespan':
        out.push({ kind: 'code', value: (token as Tokens.Codespan).text });
        break;
      case 'link': {
        const link = token as Tokens.Link;
        out.push({ kind: 'link', href: safeHref(link.href), children: inlineFrom(link.tokens) });
        break;
      }
      case 'br':
        out.push({ kind: 'break' });
        break;
      case 'image':
        // Images are not part of the article vocabulary; keeping the alt text
        // means nothing the author wrote silently disappears.
        out.push({ kind: 'text', value: (token as Tokens.Image).text });
        break;
      case 'html':
        // Literal text, never markup — see the module note on raw HTML.
        out.push({ kind: 'text', value: (token as Tokens.HTML).raw });
        break;
      default: {
        const text = token as Tokens.Text;
        // `text` tokens carry nested inline tokens inside list items.
        if (text.tokens?.length) out.push(...inlineFrom(text.tokens));
        else if (text.text) out.push({ kind: 'text', value: text.text });
      }
    }
  }

  return out;
}

// ─── Block tokens ────────────────────────────────────────────────────────────

function listFrom(token: Tokens.List): MdList {
  return {
    ordered: token.ordered,
    items: token.items.map((item) => {
      const children: MdInline[] = [];
      const lists: MdList[] = [];
      for (const child of item.tokens ?? []) {
        if (child.type === 'list') lists.push(listFrom(child as Tokens.List));
        else if (child.type !== 'space') children.push(...inlineFrom([child]));
      }
      return { children, lists };
    }),
  };
}

function blocksFrom(tokens: Token[]): MdBlock[] {
  const out: MdBlock[] = [];

  for (const token of tokens) {
    switch (token.type) {
      case 'heading': {
        const heading = token as Tokens.Heading;
        // The article title is the page's only `h1`, so a top-level Markdown
        // heading becomes an `h2`; anything deeper collapses onto `h3`.
        out.push({ kind: heading.depth <= 2 ? 'h2' : 'h3', children: inlineFrom(heading.tokens) });
        break;
      }
      case 'paragraph':
        out.push({ kind: 'p', children: inlineFrom((token as Tokens.Paragraph).tokens) });
        break;
      case 'blockquote': {
        const paragraphs = blocksFrom((token as Tokens.Blockquote).tokens ?? [])
          .map((block) => ('children' in block ? block.children : []))
          .filter((children) => children.length > 0);
        if (paragraphs.length) out.push({ kind: 'quote', paragraphs });
        break;
      }
      case 'list':
        out.push({ kind: 'list', ...listFrom(token as Tokens.List) });
        break;
      case 'code':
        out.push({ kind: 'code', value: (token as Tokens.Code).text });
        break;
      case 'hr':
        out.push({ kind: 'hr' });
        break;
      case 'space':
        break;
      case 'html':
        // Block-level raw HTML, kept as literal text for the same reason as inline.
        out.push({ kind: 'p', children: [{ kind: 'text', value: (token as Tokens.HTML).raw.trim() }] });
        break;
      default: {
        const inline = inlineFrom([token]);
        if (inline.length) out.push({ kind: 'p', children: inline });
      }
    }
  }

  return out;
}

// ─── References ──────────────────────────────────────────────────────────────
//
// Articles end with a bibliography, which renders as one distinct card rather
// than as more body copy. These heuristics are carried over unchanged from the
// seeder's old `normalizeBlocks`, which used to bake a `refs` block into the
// jsonb column; with Markdown as the source of truth the same detection now runs
// at render time. Two shapes exist in the source:
//
//   1. A "References" heading followed by citation paragraphs.
//   2. No heading at all — the citations just run on after the last section.
//
// The heading is authoritative when present; otherwise we fall back to matching
// the shape of the paragraphs themselves from the end of the article backward.

const REFS_HEADING = /^(references|referencias|bibliography|bibliografía)\b/i;
/** APA-ish: a parenthesized year (or "n.d.") is the reliable tell. */
const CITATION_SHAPE = /\((?:19|20)\d{2}[a-z]?(?:,[^)]*)?\)|\(n\.\s?d\.\)/i;
/** Fewer than this many in a row is more likely prose that happens to cite a year. */
const MIN_CITATION_RUN = 3;

/** Flattens a run of inline nodes back to its readable text. */
export function inlineText(nodes: MdInline[]): string {
  return nodes
    .map((node) => {
      switch (node.kind) {
        case 'text':
        case 'code':
          return node.value;
        case 'break':
          return ' ';
        default:
          return inlineText(node.children);
      }
    })
    .join('');
}

function blockText(block: MdBlock): string {
  switch (block.kind) {
    case 'p':
    case 'h2':
    case 'h3':
      return inlineText(block.children);
    case 'quote':
      return block.paragraphs.map(inlineText).join(' ');
    case 'list':
      return block.items.map((item) => inlineText(item.children)).join(' ');
    case 'refs':
      return block.items.map(inlineText).join(' ');
    case 'code':
      return block.value;
    default:
      return '';
  }
}

function looksLikeCitation(block: MdBlock): boolean {
  if (block.kind !== 'p') return false;
  const text = blockText(block).trim();
  // Real citations open with an author surname and stay short; body paragraphs
  // that mention a year mid-sentence are longer and start with ordinary prose.
  return text.length <= 400 && /^[“"(]?[A-ZÁÉÍÓÚÑ]/.test(text) && CITATION_SHAPE.test(text);
}

/** One citation per entry, whichever shape the author used below the heading:
    one paragraph each, or a single bulleted list. */
function toCitationItems(block: MdBlock): MdInline[][] {
  switch (block.kind) {
    case 'p':
      return [block.children];
    case 'list':
      return block.items.map((item) => item.children);
    case 'quote':
      return block.paragraphs;
    default:
      return [];
  }
}

/** Collapses a trailing bibliography into a single `refs` block, if there is one. */
function splitReferences(blocks: MdBlock[]): MdBlock[] {
  const headingIdx = blocks.findIndex(
    (block) => (block.kind === 'h2' || block.kind === 'h3') && REFS_HEADING.test(blockText(block).trim())
  );

  if (headingIdx !== -1) {
    // The section runs to the next heading: a bibliography has no sub-headings,
    // so one marks the end of it. Anything past that keeps rendering as body
    // copy below the card rather than being swallowed as a phantom citation.
    let end = headingIdx + 1;
    while (end < blocks.length && blocks[end].kind !== 'h2' && blocks[end].kind !== 'h3') end += 1;

    const items = blocks.slice(headingIdx + 1, end).flatMap(toCitationItems);
    if (items.length) return [...blocks.slice(0, headingIdx), { kind: 'refs', items }, ...blocks.slice(end)];
    return blocks;
  }

  // Walk back from the end while the paragraphs keep looking like citations.
  let start = blocks.length;
  while (start > 0 && looksLikeCitation(blocks[start - 1])) start -= 1;
  if (blocks.length - start < MIN_CITATION_RUN) return blocks;

  return [...blocks.slice(0, start), { kind: 'refs', items: blocks.slice(start).flatMap(toCitationItems) }];
}

// ─── Entry points ────────────────────────────────────────────────────────────

/** Parses an article body. Empty or whitespace-only input yields no blocks, so a
    post whose translation has not been written renders an empty body rather than
    failing — Markdown itself has no parse errors to surface. */
export function parsePostMarkdown(markdown: string | null | undefined): PostDocument {
  const source = (markdown ?? '').trim();
  if (!source) return { blocks: [] };
  return { blocks: splitReferences(blocksFrom(marked.lexer(source, { gfm: false }))) };
}

/** Rough reading time from the article's own words — derived, never hardcoded. */
export function readingMinutes(doc: PostDocument): number {
  const words = doc.blocks.reduce((n, block) => n + blockText(block).split(/\s+/).filter(Boolean).length, 0);
  return Math.max(1, Math.round(words / 200));
}
